import { useEffect, useRef, useState, type FormEvent, type RefObject } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Avatar,
  Banner,
  BannerBar,
  Button,
  Disclosure,
  Modal,
  MonoLabel,
  Pill,
  Popover,
  Select,
  Skeleton,
  TextField,
  Toast
} from '../components/primitives';
import type { PillStatus, SelectOption } from '../components/primitives';
import {
  useMembersVM,
  type MemberRow,
  type RowActionVisibility,
  type UseMembersVM
} from '../lib/vm/useMembersVM';
import { useSeatUsage } from '../lib/vm/useSeatUsage';
import { useSuspendedMembers } from '../lib/vm/useSuspendedMembers';
import { useCapability } from '../lib/vm/useCapability';
import { formatRelativeTime } from '../lib/relativeTime';
import './Members.css';

/** Sentinel value the server uses for "uncapped" seats on the $500 tier
 *  (`subscription_tier_config.max_seats`). Renders as `∞` in the seat
 *  counter. */
const UNCAPPED_MAX_SEATS = 2_147_483_647;

/**
 * `/members` page — roster (turn 2a.1.1 = read view; turn 2a.2 = capability
 * gating). Renders one unified table with Active + Pending + Expired rows
 * distinguished by the Status column. Header carries the `Invite member` +
 * `Transfer ownership` buttons; per-row Actions column carries Edit / Remove
 * (Active) or Resend / Re-invite / Revoke (Pending/Expired). Visibility is
 * gated by `useMembersVM`'s capability flags + `rowActions(row)`. The buttons
 * themselves are disabled no-ops in this turn — wiring lands in 2b.* / 2c.*.
 */
export function Members() {
  const navigate = useNavigate();
  const tableRef = useRef<HTMLTableElement>(null);

  // Seat usage + billing capability for the seat-counter pill, the disabled
  // Invite button gate, and the at-limit banner. `useSeatUsage` no-ops when
  // `orgId` is null (loading), so we can call it unconditionally.
  //
  // `useMembersVM` doesn't take an orgId — it resolves it internally — so we
  // re-derive the active org via a parallel useSeatUsage(null) pattern: the
  // VM is constructed first, then we read `vm.activeOrgId` for the seat hook.
  // We pass `onInviteSuccess` so a successful invite triggers a seat refetch
  // immediately (cluster 5G fix — pending invitations consume a seat but
  // don't fire the realtime subscription `useSeatUsage` watches).
  const seatsRef = useRef<{ refetch: () => Promise<void> } | null>(null);
  const vm = useMembersVM({
    onInviteSuccess: () => {
      void seatsRef.current?.refetch();
    }
  });
  const seats = useSeatUsage(vm.activeOrgId);
  seatsRef.current = seats;
  const billing = useCapability(vm.activeOrgId, 'billing.manage');
  // 5C — at-limit banner is dismissible per-session (state lives in this
  // component; remounts on next sign-in restore the banner when still
  // at-limit). Owner + Admin only — Members never see this banner.
  const [bannerDismissed, setBannerDismissed] = useState(false);

  const scrollEmail = vm.inviteScrollToEmail;
  const lastScrolledRef = useRef<string | null>(null);
  useEffect(() => {
    if (!scrollEmail) return;
    if (lastScrolledRef.current === scrollEmail) return;
    lastScrolledRef.current = scrollEmail;
    const escaped =
      typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(scrollEmail) : scrollEmail;
    const row = tableRef.current?.querySelector<HTMLTableRowElement>(
      `tr[data-email="${escaped}"]`
    );
    if (row) row.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [scrollEmail]);

  const loading = vm.status === 'loading';
  const errored = vm.status === 'error';
  const empty = vm.status === 'empty';

  const meta = (() => {
    if (loading) return '…';
    if (empty) return 'No org yet';
    return `${vm.activeCount} active, ${vm.pendingCount} pending`;
  })();

  const showActionsColumn =
    vm.status === 'ready' &&
    (vm.capabilities.canInvite ||
      vm.capabilities.canEditRole ||
      vm.capabilities.canRemove);

  const seatsReady = !seats.loading && seats.tier !== null;
  const seatCounterLabel = (() => {
    if (!seatsReady) return null;
    const maxLabel =
      seats.maxSeats >= UNCAPPED_MAX_SEATS ? '∞' : String(seats.maxSeats);
    return `${seats.usedSeats} of ${maxLabel} seats used`;
  })();
  const inviteBlockedBySeats = seatsReady && !seats.canAddSeat;
  const atLimitBannerVisible =
    inviteBlockedBySeats &&
    billing.allowed &&
    !bannerDismissed &&
    vm.capabilities.canInvite;

  return (
    <section
      className="mem-page"
      data-testid="members-page"
      data-viewer-role={vm.viewerPermissionSystemKey ?? ''}
      data-scroll-to-email={vm.inviteScrollToEmail ?? ''}
    >
      <header className="mem-header">
        <div>
          <h1 className="mem-h1">Members</h1>
          <MonoLabel
            size="sm"
            tracking="wide"
            tone="muted"
            data-testid="members-meta"
          >
            {meta}
          </MonoLabel>
          {vm.activeOrgName ? (
            <p className="mem-org" data-testid="members-org">
              {vm.activeOrgName}
            </p>
          ) : null}
        </div>
        <div className="mem-header-actions">
          {seatCounterLabel ? (
            <Pill
              status={inviteBlockedBySeats ? 'suspended' : 'active'}
              data-testid="members-seat-counter"
            >
              {seatCounterLabel}
            </Pill>
          ) : null}
          {vm.capabilities.canTransfer ? (
            <Button
              variant="default"
              size="sm"
              onClick={vm.openTransferModal}
              aria-expanded={vm.transferOpen}
              data-testid="members-transfer-btn"
            >
              Transfer ownership
            </Button>
          ) : null}
          {vm.capabilities.canInvite ? (
            <InviteButtonGate
              disabled={inviteBlockedBySeats}
              maxSeats={seats.maxSeats}
              billingAllowed={billing.allowed}
              inviteFormOpen={vm.inviteFormOpen}
              onClick={vm.openInviteForm}
              onUpgradeClick={() => navigate('/settings/billing')}
            />
          ) : null}
        </div>
      </header>

      {atLimitBannerVisible ? (
        <BannerBar
          kind="warn"
          title={`${seats.usedSeats} of ${
            seats.maxSeats >= UNCAPPED_MAX_SEATS ? '∞' : seats.maxSeats
          } seats used`}
          data-testid="members-at-limit-banner"
          actions={
            <>
              <Button
                size="sm"
                variant="primary"
                onClick={() => navigate('/settings/billing')}
                data-testid="members-at-limit-banner-upgrade"
              >
                Manage billing
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setBannerDismissed(true)}
                aria-label="Dismiss seat-limit banner"
                data-testid="members-at-limit-banner-dismiss"
              >
                ×
              </Button>
            </>
          }
        >
          Upgrade in the web app to add more members.
        </BannerBar>
      ) : null}

      {vm.inviteFormOpen ? <InviteForm vm={vm} /> : null}

      {loading ? (
        <div className="mem-skeleton" data-testid="members-skeleton">
          <Skeleton width="100%" height={32} />
          <Skeleton width="100%" height={32} />
          <Skeleton width="100%" height={32} />
        </div>
      ) : errored ? (
        <Banner kind="error" title="Couldn't load members">
          {vm.error ?? 'Refresh the page or sign out and back in.'}
        </Banner>
      ) : empty ? (
        <Banner kind="default" title="No active org">
          Join or create an org to manage members.
        </Banner>
      ) : (
        <>
          <MembersTable
            tableRef={tableRef}
            rows={vm.rows}
            canSeeEmails={vm.canSeeEmails}
            showActionsColumn={showActionsColumn}
            rowActions={vm.rowActions}
            vm={vm}
          />
          {seatsReady && seats.suspended > 0 && billing.allowed ? (
            <SuspendedMembersSection
              orgId={vm.activeOrgId}
              suspendedCount={seats.suspended}
            />
          ) : null}
        </>
      )}

      {vm.inviteSentTo ? (
        <Toast
          title={vm.inviteEdgeFailed ? 'Invitation created' : 'Invitation sent'}
          durationMs={6000}
          onDismiss={vm.clearInviteToast}
        >
          {vm.inviteEdgeFailed
            ? 'Invitation row created but email delivery failed.'
            : `Invitation sent to ${vm.inviteSentTo}.`}
        </Toast>
      ) : null}

      {vm.editSavedFor ? (
        <Toast
          title="Role updated"
          durationMs={6000}
          onDismiss={vm.clearEditToast}
        >
          {(() => {
            const r = vm.rows.find((x) => x.user_id === vm.editSavedFor);
            const who = r?.display_name ?? r?.email ?? 'member';
            return `Updated ${who}'s role.`;
          })()}
        </Toast>
      ) : null}

      {vm.promoteSavedFor ? (
        <Toast
          title="Owner promoted"
          durationMs={6000}
          onDismiss={vm.clearPromoteToast}
        >
          {`Promoted ${vm.promoteConfirmName ?? 'member'} to Owner.`}
        </Toast>
      ) : null}

      {vm.transferSavedName ? (
        <Toast
          title="Ownership transferred"
          durationMs={6000}
          onDismiss={vm.clearTransferToast}
        >
          {`Transferred ownership to ${vm.transferSavedName}.`}
        </Toast>
      ) : null}

      {vm.removeSavedName ? (
        <Toast
          title="Member removed"
          durationMs={6000}
          onDismiss={vm.clearRemoveToast}
        >
          {`Removed ${vm.removeSavedName}.`}
        </Toast>
      ) : null}

      {vm.resendSavedEmail ? (
        <Toast
          title={vm.resendEdgeFailed ? 'Email delivery failed' : 'Invitation resent'}
          durationMs={6000}
          onDismiss={vm.clearResendToast}
        >
          {vm.resendEdgeFailed
            ? 'Invitation resent but email delivery failed.'
            : `Invitation resent to ${vm.resendSavedEmail}.`}
        </Toast>
      ) : null}

      <PromoteConfirmModal vm={vm} />
      <TransferOwnershipModal vm={vm} />
      <RemoveMemberModal vm={vm} />
    </section>
  );
}

interface RemoveMemberModalProps {
  vm: UseMembersVM;
}

function RemoveMemberModal({ vm }: RemoveMemberModalProps) {
  const open = vm.removeTargetUserId !== null;
  const name = vm.removeTargetName ?? 'this member';
  const orgName = vm.activeOrgName ?? 'this org';
  const submitting = vm.removeStatus === 'submitting';
  return (
    <Modal
      open={open}
      onClose={vm.cancelRemove}
      title={`Remove ${name} from ${orgName}?`}
      dataTestId="members-remove-modal"
      footer={
        <>
          <Button
            type="button"
            variant="default"
            size="sm"
            onClick={vm.cancelRemove}
            disabled={submitting}
            data-testid="members-remove-cancel"
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={() => void vm.confirmRemove()}
            disabled={submitting}
            data-testid="members-remove-confirm"
          >
            {submitting ? 'Removing…' : 'Remove'}
          </Button>
        </>
      }
    >
      <p className="mem-remove-copy" data-testid="members-remove-copy">
        They'll lose access immediately. You can re-invite them later.
      </p>
      {vm.removeStatus === 'error' && vm.removeError ? (
        <p
          className="mem-remove-error"
          role="alert"
          data-testid="members-remove-error"
        >
          {vm.removeError}
        </p>
      ) : null}
    </Modal>
  );
}

interface TransferOwnershipModalProps {
  vm: UseMembersVM;
}

function TransferOwnershipModal({ vm }: TransferOwnershipModalProps) {
  const submitting = vm.transferStatus === 'submitting';
  const options: SelectOption[] = vm.transferTargetOptions.map((o) => ({
    value: o.userId,
    label: o.label
  }));
  const canConfirm = !!vm.transferTargetUserId && !submitting;
  return (
    <Modal
      open={vm.transferOpen}
      onClose={vm.cancelTransferModal}
      title="Transfer ownership"
      dataTestId="members-transfer-modal"
      footer={
        <>
          <Button
            type="button"
            variant="default"
            size="sm"
            onClick={vm.cancelTransferModal}
            disabled={submitting}
            data-testid="members-transfer-cancel"
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={() => void vm.confirmTransfer()}
            disabled={!canConfirm}
            data-testid="members-transfer-confirm"
          >
            {submitting ? 'Transferring…' : 'Transfer ownership'}
          </Button>
        </>
      }
    >
      <p className="mem-transfer-copy" data-testid="members-transfer-copy">
        Pick a member to make the new Owner. You'll be demoted to Admin and
        lose Owner-only capabilities.
      </p>
      <Select
        id="members-transfer-target"
        label="New owner"
        placeholder="Choose a member"
        options={options}
        value={vm.transferTargetUserId}
        onChange={(e) => vm.setTransferTargetUserId(e.currentTarget.value)}
        disabled={submitting}
        data-testid="members-transfer-target"
      />
      {vm.transferStatus === 'error' && vm.transferError ? (
        <p
          className="mem-transfer-error"
          role="alert"
          data-testid="members-transfer-error"
        >
          {vm.transferError}
        </p>
      ) : null}
    </Modal>
  );
}

interface PromoteConfirmModalProps {
  vm: UseMembersVM;
}

function PromoteConfirmModal({ vm }: PromoteConfirmModalProps) {
  const open = vm.promoteConfirmTargetUserId !== null;
  const name = vm.promoteConfirmName ?? 'member';
  const submitting = vm.promoteConfirmStatus === 'submitting';
  return (
    <Modal
      open={open}
      onClose={vm.cancelPromoteToOwner}
      title={`Promote ${name} to Owner?`}
      dataTestId="members-promote-modal"
      footer={
        <>
          <Button
            type="button"
            variant="default"
            size="sm"
            onClick={vm.cancelPromoteToOwner}
            disabled={submitting}
            data-testid="members-promote-cancel"
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={() => void vm.confirmPromoteToOwner()}
            disabled={submitting}
            data-testid="members-promote-confirm"
          >
            {submitting ? 'Promoting…' : 'Promote to Owner'}
          </Button>
        </>
      }
    >
      <p className="mem-promote-copy" data-testid="members-promote-copy">
        Owners can manage every aspect of the org. You'll be demoted to Admin.
      </p>
      {vm.promoteConfirmStatus === 'error' && vm.promoteConfirmError ? (
        <p
          className="mem-promote-error"
          role="alert"
          data-testid="members-promote-error"
        >
          {vm.promoteConfirmError}
        </p>
      ) : null}
    </Modal>
  );
}

interface MembersTableProps {
  tableRef: RefObject<HTMLTableElement>;
  rows: ReadonlyArray<MemberRow>;
  canSeeEmails: boolean;
  showActionsColumn: boolean;
  rowActions: (row: MemberRow) => RowActionVisibility;
  vm: UseMembersVM;
}

function MembersTable({ tableRef, rows, canSeeEmails, showActionsColumn, rowActions, vm }: MembersTableProps) {
  const columnCount = 5 + (canSeeEmails ? 1 : 0) + (showActionsColumn ? 1 : 0);
  return (
    <table ref={tableRef} className="mem-table" data-testid="members-table">
      <thead>
        <tr>
          <th scope="col">
            <MonoLabel size="xs" tracking="wider">Member</MonoLabel>
          </th>
          {canSeeEmails ? (
            <th scope="col" data-testid="members-col-email">
              <MonoLabel size="xs" tracking="wider">Email</MonoLabel>
            </th>
          ) : null}
          <th scope="col">
            <MonoLabel size="xs" tracking="wider">Permission</MonoLabel>
          </th>
          <th scope="col">
            <MonoLabel size="xs" tracking="wider">Function</MonoLabel>
          </th>
          <th scope="col">
            <MonoLabel size="xs" tracking="wider">Status</MonoLabel>
          </th>
          <th scope="col">
            <MonoLabel size="xs" tracking="wider">Activity</MonoLabel>
          </th>
          {showActionsColumn ? (
            <th scope="col" data-testid="members-col-actions">
              <MonoLabel size="xs" tracking="wider">Actions</MonoLabel>
            </th>
          ) : null}
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <tr>
            <td
              colSpan={columnCount}
              className="mem-empty"
              data-testid="members-empty"
            >
              No members yet.
            </td>
          </tr>
        ) : (
          rows.map((r) => (
            <MemberRowView
              key={r.kind === 'active' ? `m-${r.user_id}` : `i-${r.invitation_id}`}
              row={r}
              canSeeEmails={canSeeEmails}
              showActionsColumn={showActionsColumn}
              actions={rowActions(r)}
              vm={vm}
            />
          ))
        )}
      </tbody>
    </table>
  );
}

interface InviteFormProps {
  vm: UseMembersVM;
}

function InviteForm({ vm }: InviteFormProps) {
  const loading = vm.inviteStatus === 'loading-options';
  const optionsErrored = vm.inviteStatus === 'options-error';
  const submitting = vm.inviteStatus === 'submitting';

  const permissionOptions: SelectOption[] = vm.invitePermissionRoleOptions.map(
    (p) => ({
      value: p.id,
      label: p.name
    })
  );
  const functionOptions: SelectOption[] = vm.functionRoleOptions.map((f) => ({
    value: f.id,
    label: f.label
  }));

  const emailDirty = vm.inviteEmail.length > 0;
  const clientEmailError =
    emailDirty && !vm.inviteEmailValid ? 'Enter a valid email address.' : null;
  const serverError = vm.inviteStatus === 'error' ? vm.inviteError : null;
  const emailError = clientEmailError ?? serverError;

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    void vm.submitInvite();
  }

  return (
    <form
      id="members-invite-form"
      className="mem-invite-form"
      data-testid="members-invite-form"
      aria-labelledby="mem-invite-heading"
      onSubmit={handleSubmit}
      noValidate
    >
      <h2 id="mem-invite-heading" className="mem-invite-heading">
        Invite member
      </h2>
      {loading ? (
        <p className="mem-invite-loading" data-testid="members-invite-loading">
          Loading options…
        </p>
      ) : optionsErrored ? (
        <Banner kind="error" title="Couldn't load invite options">
          {vm.inviteError ?? 'Refresh the page and try again.'}
        </Banner>
      ) : (
        <div className="mem-invite-grid">
          <TextField
            id="members-invite-email"
            type="email"
            label="Email"
            placeholder="name@org.com"
            value={vm.inviteEmail}
            onChange={(e) => vm.setInviteEmail(e.currentTarget.value)}
            errorText={emailError ?? undefined}
            autoComplete="off"
            data-testid="members-invite-email"
          />
          <Select
            id="members-invite-permission"
            label="Permission role"
            placeholder="Choose a permission role"
            options={permissionOptions}
            value={vm.invitePermissionRoleId}
            onChange={(e) => vm.setInvitePermissionRoleId(e.currentTarget.value)}
            data-testid="members-invite-permission"
          />
          <Select
            id="members-invite-function"
            label="Function role"
            placeholder="Choose a function role"
            options={functionOptions}
            value={vm.inviteFunctionRoleId}
            onChange={(e) => vm.setInviteFunctionRoleId(e.currentTarget.value)}
            data-testid="members-invite-function"
          />
        </div>
      )}
      <div className="mem-invite-actions">
        <Button
          type="button"
          variant="default"
          size="sm"
          onClick={vm.cancelInviteForm}
          data-testid="members-invite-cancel"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          size="sm"
          disabled={!vm.inviteFormCanSubmit || submitting}
          data-testid="members-invite-submit"
        >
          {submitting ? 'Sending…' : 'Send invitation'}
        </Button>
      </div>
    </form>
  );
}

const PERM_LABEL: Record<string, string> = {
  owner: 'Owner',
  admin: 'Admin',
  member: 'Member'
};

const STATUS_PILL: Record<MemberRow['kind'], { status: PillStatus; label: string }> = {
  active: { status: 'active', label: 'active' },
  pending: { status: 'pending', label: 'pending' },
  expired: { status: 'expired', label: 'expired' }
};

interface MemberRowViewProps {
  row: MemberRow;
  canSeeEmails: boolean;
  showActionsColumn: boolean;
  actions: RowActionVisibility;
  vm: UseMembersVM;
}

function MemberRowView({ row, canSeeEmails, showActionsColumn, actions, vm }: MemberRowViewProps) {
  const isInvite = row.kind !== 'active';
  const display = row.display_name ?? row.email ?? '—';
  const handle = row.username
    ? `@${row.username}#${String(row.discriminator ?? 0).padStart(4, '0').slice(-4)}`
    : null;
  const permLabel =
    (row.permission_role_system_key && PERM_LABEL[row.permission_role_system_key]) ??
    row.permission_role_name ??
    '—';
  const pill = STATUS_PILL[row.kind];
  const activity =
    row.kind === 'active'
      ? formatRelativeTime(row.last_active_at)
      : `Invited ${formatRelativeTime(row.invited_at)}`;

  const isEditing =
    row.kind === 'active' &&
    row.user_id !== null &&
    vm.editingUserId === row.user_id;
  const editPermissionOptions: SelectOption[] = vm.editPermissionRoleOptions.map(
    (p) => ({
      value: p.id,
      label: p.name
    })
  );
  const editFunctionOptions: SelectOption[] = vm.functionRoleOptions.map((f) => ({
    value: f.id,
    label: f.label
  }));
  const editLoading = vm.editStatus === 'loading-options';
  const editSaving = vm.editStatus === 'saving';

  return (
    <tr
      className={`mem-row mem-row-${row.kind}${isEditing ? ' mem-row-editing' : ''}`}
      data-testid={`members-row`}
      data-kind={row.kind}
      data-user-id={row.user_id ?? ''}
      data-invitation-id={row.invitation_id ?? ''}
      data-email={row.email ?? ''}
      data-editing={isEditing ? 'true' : 'false'}
    >
      <td>
        <div className="mem-name-cell">
          <Avatar name={isInvite ? null : display} size="md" />
          <div className="mem-name-stack">
            <span className="mem-name">
              {display}
              {row.is_self ? (
                <span
                  className="mem-you-pill"
                  data-testid="members-you-pill"
                >
                  You
                </span>
              ) : null}
            </span>
            {handle ? <span className="mem-handle">{handle}</span> : null}
          </div>
        </div>
      </td>
      {canSeeEmails ? (
        <td className="mem-email" data-testid="members-cell-email">
          {row.email ?? ''}
        </td>
      ) : null}
      <td>
        {isEditing ? (
          <Select
            id={`members-edit-permission-${row.user_id ?? ''}`}
            label="Permission role"
            hideLabel
            placeholder="Choose"
            options={editPermissionOptions}
            value={vm.editPermissionRoleId}
            disabled={editLoading || editSaving}
            onChange={(e) => vm.setEditPermissionRoleId(e.currentTarget.value)}
            data-testid="members-edit-permission"
          />
        ) : (
          permLabel
        )}
      </td>
      <td>
        {isEditing ? (
          <Select
            id={`members-edit-function-${row.user_id ?? ''}`}
            label="Function role"
            hideLabel
            placeholder="Choose"
            options={editFunctionOptions}
            value={vm.editFunctionRoleId}
            disabled={editLoading || editSaving}
            onChange={(e) => vm.setEditFunctionRoleId(e.currentTarget.value)}
            data-testid="members-edit-function"
          />
        ) : (
          row.function_role_label ?? '—'
        )}
      </td>
      <td>
        <Pill status={pill.status} data-testid="members-status">
          {pill.label}
        </Pill>
      </td>
      <td className="mem-activity">{activity}</td>
      {showActionsColumn ? (
        <td className="mem-actions-cell">
          <div className="mem-actions" data-testid="members-row-actions">
            {isEditing ? (
              <>
                <Button
                  size="sm"
                  variant="default"
                  onClick={vm.cancelEditRole}
                  disabled={editSaving}
                  data-testid="members-edit-cancel"
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  variant="primary"
                  onClick={() => void vm.saveRoleChange()}
                  disabled={editLoading || editSaving}
                  data-testid="members-edit-save"
                >
                  {editSaving ? 'Saving…' : 'Save'}
                </Button>
              </>
            ) : actions.edit ? (
              <Button
                size="sm"
                variant="default"
                onClick={() => vm.startEditRole(row)}
                data-testid="members-action-edit"
              >
                Edit
              </Button>
            ) : null}
            {actions.remove ? (
              <Button
                size="sm"
                variant="default"
                onClick={() => vm.requestRemove(row)}
                data-testid="members-action-remove"
              >
                Remove
              </Button>
            ) : null}
            {actions.resend ? (() => {
              const rs = vm.resendStateFor(row);
              const label = rs.busy
                ? 'Resending…'
                : rs.cooldownSeconds > 0
                  ? `Wait ${rs.cooldownSeconds}s`
                  : 'Resend';
              return (
                <Button
                  size="sm"
                  variant="default"
                  disabled={rs.busy || rs.cooldownSeconds > 0}
                  onClick={() => void vm.resendInvitation(row)}
                  data-testid="members-action-resend"
                >
                  {label}
                </Button>
              );
            })() : null}
            {actions.reinvite ? (
              <Button
                size="sm"
                variant="default"
                disabled
                data-testid="members-action-reinvite"
              >
                Re-invite
              </Button>
            ) : null}
            {actions.revoke ? (
              <Button
                size="sm"
                variant="default"
                disabled
                data-testid="members-action-revoke"
              >
                Revoke
              </Button>
            ) : null}
          </div>
          {isEditing && vm.editStatus === 'error' && vm.editError ? (
            <span
              className="mem-edit-error"
              role="alert"
              data-testid="members-edit-error"
            >
              {vm.editError}
            </span>
          ) : null}
          {isEditing && vm.editStatus === 'options-error' && vm.editError ? (
            <span
              className="mem-edit-error"
              role="alert"
              data-testid="members-edit-options-error"
            >
              {vm.editError}
            </span>
          ) : null}
          {actions.resend && vm.resendStateFor(row).error ? (
            <span
              className="mem-resend-error"
              role="alert"
              data-testid="members-resend-error"
            >
              {vm.resendStateFor(row).error}
            </span>
          ) : null}
        </td>
      ) : null}
    </tr>
  );
}

interface InviteButtonGateProps {
  disabled: boolean;
  maxSeats: number;
  billingAllowed: boolean;
  inviteFormOpen: boolean;
  onClick: () => void;
  onUpgradeClick: () => void;
}

/**
 * The Invite-member button + at-limit tooltip. When `disabled` the button is
 * not clickable; the tooltip popover surfaces the seat-limit reason + (for
 * Owner/Admin) a `Manage billing` CTA. Members without `billing.manage` see
 * the "Contact your Owner" copy with no link.
 *
 * Uses `<Popover>` for the tooltip because the existing primitives don't
 * include a hover-tooltip and we don't want to add one for this single use —
 * the popover-on-click pattern matches the IDE spec's "hover/focus shows the
 * tooltip" behavior closely enough (click also reveals it on touch surfaces).
 */
function InviteButtonGate({
  disabled,
  maxSeats,
  billingAllowed,
  inviteFormOpen,
  onClick,
  onUpgradeClick
}: InviteButtonGateProps) {
  if (!disabled) {
    return (
      <Button
        variant="primary"
        size="sm"
        onClick={onClick}
        aria-expanded={inviteFormOpen}
        aria-controls="members-invite-form"
        data-testid="members-invite-btn"
      >
        Invite member
      </Button>
    );
  }
  const maxLabel = maxSeats >= UNCAPPED_MAX_SEATS ? '∞' : String(maxSeats);
  return (
    <Popover
      label="Seat limit reached"
      align="end"
      data-testid="members-invite-limit-popover"
      trigger={(api) => (
        <Button
          variant="primary"
          size="sm"
          aria-disabled
          onClick={api.onClick}
          aria-haspopup={api['aria-haspopup']}
          aria-expanded={api['aria-expanded']}
          data-testid="members-invite-btn"
          data-invite-disabled="true"
        >
          Invite member
        </Button>
      )}
    >
      {({ close }) =>
        billingAllowed ? (
          <div className="mem-invite-tooltip">
            <p className="mem-invite-tooltip-copy">
              You've used all {maxLabel} seats. Upgrade in the web app to add
              more.
            </p>
            <div className="mem-invite-tooltip-actions">
              <Button
                variant="primary"
                size="sm"
                onClick={() => {
                  close();
                  onUpgradeClick();
                }}
                data-testid="members-invite-limit-upgrade"
              >
                Manage billing
              </Button>
            </div>
          </div>
        ) : (
          <div className="mem-invite-tooltip">
            <p className="mem-invite-tooltip-copy">
              Your org has used all {maxLabel} seats. Contact your Owner to add
              more.
            </p>
          </div>
        )
      }
    </Popover>
  );
}

interface SuspendedMembersSectionProps {
  orgId: string | null;
  suspendedCount: number;
}

/**
 * Suspended members disclosure rendered below the active members list. The
 * `useSuspendedMembers` RPC is mounted only when the disclosure is opened
 * (`fetchKey` flips), keeping the page's initial render free of an extra
 * round-trip when no suspended members exist or the viewer hasn't expanded the
 * section. Rows are visually muted; FIFO order (oldest `suspended_at` first)
 * is the server's responsibility.
 */
function SuspendedMembersSection({
  orgId,
  suspendedCount
}: SuspendedMembersSectionProps) {
  const [open, setOpen] = useState(false);
  const suspended = useSuspendedMembers(open ? orgId : null);
  return (
    <Disclosure
      summary={`Suspended (${suspendedCount})`}
      open={open}
      onToggle={setOpen}
      dataTestId="members-suspended-disclosure"
      className="mem-suspended"
    >
      {suspended.loading ? (
        <div
          className="mem-suspended-loading"
          data-testid="members-suspended-loading"
        >
          <Skeleton width="100%" height={24} />
          <Skeleton width="100%" height={24} />
        </div>
      ) : suspended.error ? (
        <Banner kind="error" title="Couldn't load suspended members">
          {suspended.error}
        </Banner>
      ) : suspended.members.length === 0 ? (
        <p
          className="mem-suspended-empty"
          data-testid="members-suspended-empty"
        >
          No suspended members.
        </p>
      ) : (
        <ul
          className="mem-suspended-list"
          data-testid="members-suspended-list"
        >
          {suspended.members.map((m) => {
            const display = m.display_name ?? m.email ?? '—';
            const since = formatRelativeTime(m.suspended_at);
            return (
              <li
                key={m.user_id}
                className="mem-suspended-row"
                data-testid="members-suspended-row"
                data-user-id={m.user_id}
              >
                <div className="mem-suspended-row-main">
                  <span className="mem-suspended-name">{display}</span>
                  {m.permission_role_name ? (
                    <span className="mem-suspended-role">
                      {m.permission_role_name}
                    </span>
                  ) : null}
                </div>
                <span className="mem-suspended-since">
                  Suspended {since}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </Disclosure>
  );
}
